/**
 * Display helpers of the Customer Accounts container (C22). Display-only arithmetic over decimal strings with BigInt
 * (no `number` for money, ADR-02 §2.9): balance after an adjustment, the dual statement's net difference, ordering.
 * Every posted figure comes from the server (preview `derived`, the read model).
 */
import { formatWithCommas } from "@faylasuf/ui";
import type { Translate } from "@faylasuf/i18n";
import type { CustomerAccountRow, CustomerKind } from "../../api/types.js";

const SCALE = 6;
const abs = (s: string): string => s.replace(/^-/, "");

export function toScaled(s: string): bigint {
  const neg = s.startsWith("-");
  const [i = "0", f = ""] = abs(s.trim() || "0").split(".");
  const v = BigInt(i || "0") * 10n ** BigInt(SCALE) + BigInt((f + "000000").slice(0, SCALE) || "0");
  return neg ? -v : v;
}

export function fromScaled(v: bigint): string {
  const neg = v < 0n;
  const a = neg ? -v : v;
  const base = 10n ** BigInt(SCALE);
  const frac = (a % base).toString().padStart(SCALE, "0").replace(/0+$/, "");
  return `${neg ? "-" : ""}${(a / base).toString()}${frac ? `.${frac}` : ""}`;
}

export const decAdd = (a: string, b: string): string => fromScaled(toScaled(a) + toScaled(b));
export const decSub = (a: string, b: string): string => fromScaled(toScaled(a) - toScaled(b));
export const decCmp = (a: string, b: string): number => {
  const d = toScaled(a) - toScaled(b);
  return d < 0n ? -1 : d > 0n ? 1 : 0;
};
export const isZero = (s: string): boolean => toScaled(s) === 0n;
export const positive = (s: string): boolean => /^\d*\.?\d+$/.test(s) && /[1-9]/.test(s);

/** "1,234" with the sign kept ("-1,234"). */
export function fmt(s: string): string {
  return s.startsWith("-") ? `-${formatWithCommas(abs(s))}` : formatWithCommas(s);
}
export const fmtAbs = (s: string): string => formatWithCommas(abs(s));

export function balanceOf(row: CustomerAccountRow, cur: "IQD" | "USD"): string {
  return cur === "USD" ? row.usd.balance : row.iqd.balance;
}

export function accountIdOf(row: CustomerAccountRow, cur: "IQD" | "USD"): string {
  return cur === "USD" ? row.usd.accountId : row.iqd.accountId;
}

export function kindLabel(t: Translate, k: CustomerKind): string {
  return k === "debt" ? t("cust.debt_one") : t("cust.trust_one");
}

/** "1,000,000 د.ع | 50 $" (old modals' balance line; the USD part only while USD is shown). */
export function balancesLine(t: Translate, row: CustomerAccountRow, usdHidden: boolean): string {
  return `${fmt(row.iqd.balance)} ${t("unit.iqd")}${usdHidden ? "" : ` | ${fmt(row.usd.balance)} $`}`;
}

/**
 * Old `customerActivityMap` categories (App.tsx:23604-23700) by operation type: vault, store, currency, transfer,
 * debt adjustment, other.
 */
export function activityKey(type: string): string {
  if (type === "quick_deposit") return "cust.act_vault_deposit";
  if (type === "quick_withdraw") return "cust.act_vault_withdraw";
  if (type.startsWith("store_") || type.startsWith("installment_")) return "cust.act_store";
  if (type === "usd_buy" || type === "currency_buy" || type === "transfer_in_settled") return "cust.act_currency_buy";
  if (type === "usd_sell" || type === "currency_sell" || type === "currency_sell_for_usd" || type === "transfer_out_settled") return "cust.act_currency_sell";
  if (type === "customer_transfer" || type.startsWith("transfer_")) return "cust.act_transfer";
  if (type.startsWith("customer_") || type === "account_conversion" || type === "partner_charge") return "cust.act_adjust";
  return "cust.act_other";
}

/** en-US MM-DD-YYYY in the office time zone (old `toLocaleDateString("en-US", …).replace(/\//g, "-")`). */
export function dateDashes(iso: string, timeZone: string): string {
  return new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "2-digit", day: "2-digit", timeZone }).replace(/\//g, "-");
}

/** Old last-activity line: "اليوم 12:49 م" or "10-18-2026 12:49 م" (time in the UI locale, office time zone). */
export function activityDate(iso: string, nowDate: Date, timeZone: string, locale: string, todayWord: string): string {
  const day = (d: Date) => d.toLocaleDateString("en-CA", { timeZone });
  const time = new Date(iso).toLocaleTimeString(locale === "ar" ? "ar-IQ" : "en-US", { hour: "2-digit", minute: "2-digit", timeZone });
  return day(new Date(iso)) === day(nowDate) ? `${todayWord} ${time}` : `${dateDashes(iso, timeZone)} ${time}`;
}

export function compareAccountNumbers(a: string, b: string): number {
  const na = /^\d+$/.test(a);
  const nb = /^\d+$/.test(b);
  if (na && nb) return decCmp(a, b);
  return a.localeCompare(b, "ar");
}
